import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { ArrowLeft, Check, Mail, Key } from 'lucide-react';
import { useI18n } from '../i18n';

export default function JoinWithInvite({ onBack, onSuccess }) {
  const { t } = useI18n();
  const [step, setStep] = useState('verificar');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaConfirm, setSenhaConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [conviteValido, setConviteValido] = useState(null);

  const verificarConvite = async () => {
    if (!email.trim() || !token.trim()) {
      setIsError(true);
      setMessage(t('joinInvite.fillEmailAndCode'));
      return;
    }

    setLoading(true);
    setMessage('');
    setIsError(false);

    try {
      const { data, error } = await supabase
        .rpc('verificar_convite', {
          p_email: email.trim().toLowerCase(),
          p_token: token.trim().toUpperCase()
        });

      if (error) {
        setIsError(true);
        setMessage(t('joinInvite.verifyError', { message: error.message }));
        return;
      }

      if (data && data.valido) {
        setConviteValido(data);
        setIsError(false);
        setMessage(t('joinInvite.validInvite'));
        setTimeout(() => setStep('senha'), 1000);
      } else {
        setIsError(true);
        setMessage(data?.mensagem || t('joinInvite.invalidCredentials'));
      }
    } catch (error) {
      setIsError(true);
      setMessage(t('joinInvite.verifyError', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const criarConta = async () => {
    if (!senha || senha.length < 6) {
      setIsError(true);
      setMessage(t('joinInvite.passwordMinLength'));
      return;
    }

    if (senha !== senhaConfirm) {
      setIsError(true);
      setMessage(t('joinInvite.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    setMessage('');
    setIsError(false);

    try {
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password: senha,
        options: {
          emailRedirectTo: window.location.origin
        }
      });

      if (signUpError) {
        setIsError(true);
        if (signUpError.message.includes('already registered') ||
            signUpError.message.includes('already exists') ||
            signUpError.code === 'user_already_exists') {
          setMessage(t('joinInvite.emailAlreadyExists'));
        } else {
          setMessage(t('joinInvite.signupError', { message: signUpError.message }));
        }
        return;
      }

      if (!authData.user) {
        setIsError(true);
        setMessage(t('joinInvite.userNotCreated'));
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 1000));

      if (!conviteValido.projeto_id) {
        setIsError(true);
        setMessage(t('joinInvite.projectIdMissing'));
        return;
      }

      const { data: rpcData, error: rpcError } = await supabase
        .rpc('usar_convite', {
          p_convite_id: conviteValido.convite_id,
          p_user_id: authData.user.id
        });

      if (!rpcError && rpcData && rpcData.sucesso) {
        setStep('done');
        setTimeout(() => {
          onSuccess();
        }, 2000);
        return;
      }

      const { data: insertData, error: projetoError } = await supabase
        .from('usuarios_projetos')
        .insert({
          user_id: authData.user.id,
          projeto_id: conviteValido.projeto_id
        })
        .select();

      if (projetoError) {
        setIsError(true);
        setMessage(t('joinInvite.addToProjectError', { message: projetoError.message }));
        return;
      }

      // Nothing came back: the user was not added, so keep the invite usable.
      if (!insertData || insertData.length === 0) {
        setIsError(true);
        setMessage(t('joinInvite.permissionsWarning'));
        return;
      }

      const { error: conviteError } = await supabase
        .from('convites')
        .update({ usado: true, usado_por: authData.user.id })
        .eq('id', conviteValido.convite_id);
      if (conviteError) console.error('Error marking invite as used:', conviteError);

      setStep('done');
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error) {
      setIsError(true);
      setMessage(t('joinInvite.genericError', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="bg-white p-8 rounded-xl border border-gray-200 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 bg-verde-100 text-verde-600">
            <Check size={28} />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-gray-900">{t('joinInvite.successTitle')}</h2>
          <p className="text-sm text-gray-500">{t('joinInvite.successBody')}</p>
        </div>
      </div>
    );
  }

  if (step === 'senha') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full">
          <button
            onClick={() => setStep('verificar')}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium"
          >
            <ArrowLeft size={20} />
            {t('common.back')}
          </button>

          <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-lavender-100 text-lavender-600">
                <Key size={22} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{t('joinInvite.passwordStepTitle')}</h2>
                <p className="text-xs text-gray-500">{t('joinInvite.step2of2')}</p>
              </div>
            </div>

            <div className="mb-4 p-3 rounded-lg text-sm bg-verde-100 border border-verde-200 text-verde-600">
              ✓ {t('joinInvite.inviteValidFor')} <strong>{email}</strong>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  {t('joinInvite.passwordLabel')}
                </label>
                <input
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder={t('joinInvite.passwordPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-all"
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  {t('joinInvite.confirmPasswordLabel')}
                </label>
                <input
                  type="password"
                  value={senhaConfirm}
                  onChange={(e) => setSenhaConfirm(e.target.value)}
                  placeholder={t('joinInvite.confirmPasswordPlaceholder')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-all"
                  onKeyPress={(e) => e.key === 'Enter' && criarConta()}
                />
              </div>

              {message && (
                <div className={`p-3 rounded-lg text-sm ${isError ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-verde-100 border border-verde-200 text-verde-600'}`}>
                  {message}
                </div>
              )}

              <button
                onClick={criarConta}
                disabled={loading || !senha || senha.length < 6}
                className="w-full py-3 rounded-xl font-bold bg-gray-900 hover:bg-gray-700 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('joinInvite.creatingAccount') : t('joinInvite.createAccount')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-md w-full">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium"
        >
          <ArrowLeft size={20} />
          {t('common.back')}
        </button>

        <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-lavender-100 text-lavender-600">
              <Mail size={22} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('joinInvite.step1Title')}</h2>
              <p className="text-xs text-gray-500">{t('joinInvite.step1of2')}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('joinInvite.emailLabel')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-all"
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('joinInvite.emailHelp')}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('joinInvite.codeLabel')}
              </label>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value.toUpperCase())}
                placeholder={t('joinInvite.codePlaceholder')}
                maxLength={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-all font-mono text-base text-center uppercase tracking-widest"
                onKeyPress={(e) => e.key === 'Enter' && verificarConvite()}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('joinInvite.codeHelp')}
              </p>
            </div>

            {message && (
              <div className={`p-3 rounded-lg text-sm ${isError ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-verde-100 border border-verde-200 text-verde-600'}`}>
                {message}
              </div>
            )}

            <button
              onClick={verificarConvite}
              disabled={loading || !email.trim() || !token.trim() || token.length !== 8}
              className="w-full py-3 rounded-xl font-bold bg-gray-900 hover:bg-gray-700 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('common.verifying') : t('joinInvite.verify')}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            {t('joinInvite.sentToEmail')}
          </p>
        </div>
      </div>
    </div>
  );
}
